import { useEffect, useState } from 'react';
import { User, Mail, Phone, Instagram, Plus, X, CheckCircle2, RefreshCcw, XCircle, MessageCircle, Users, TrendingUp, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence, easeInOut, } from 'framer-motion';
import DatePicker from '../components/DatePicker';
import SocialMediaModal from '../components/SocialMediaModal';
import ImagesDev from '../api/images';
import axios from "axios";
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import '../pages/events/inputStyle.css';
import { Toast } from '../components/Toast';
import { getAllUsers } from '../api/authService';
import Link from '../components/Link';
// import { getAllComments } from '@/api/adminService';
// import { supabase, BioData } from './lib/supabase';

interface SocialHandle {
  platform: string;
  handle: string;
}
export interface BioData {
  id?: string;
  name: string;
  gender: string;
  phone: string;
  email: string;
  birthday: string;
  instagram?: string;
  other_social?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  created_at?: string;
  updated_at?: string;
}


export default function BioDataForm() {
  const [formData, setFormData] = useState<BioData>({
    name: '',
    gender: '',
    phone: '',
    email: '',
    birthday: '',
    instagram: '',
    other_social: '',
    address: '',
    city: '',
    state: '',
    country: '',
  });
  const [allUsers, setAllUsers] = useState([])

  const [socialHandles, setSocialHandles] = useState<SocialHandle[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [bgImage, setBgImage] = useState("");

  useEffect(() => {
    setBgImage(getRandomHeroImage())
  }, [])

  useEffect(() => {
    const fetchUsers = async () => {
      const users = await getAllUsers();
      if (users) {
        setAllUsers(users);
      }
    };
    const fetchComments = async () => {
      // const all: any = await getAllComments();
      // if (all) setComments(all);
    };

    fetchUsers();
    fetchComments();
  }, [])

  const getRandomHeroImage = () => {
    const images = window.innerWidth > 768 ? ImagesDev.heroImagesDesk : ImagesDev.heroImages;
    return images[Math.floor(Math.random() * images.length)];
  };

  const handleInputChange = (field: keyof BioData, value: string) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleAddSocial = (platform: string, handle: string) => {
    setSocialHandles([...socialHandles, { platform, handle }]);
  };

  const handleRemoveSocial = (index: number) => {
    setSocialHandles(socialHandles.filter((_, i) => i !== index));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const name = formData?.name?.trim() || '';
    const gender = formData?.gender?.trim() || '';
    const phone = formData?.phone?.trim() || '';
    const email = formData?.email?.trim() || '';
    const birthday = formData?.birthday?.trim() || '';
    const address = formData?.address?.trim() || '';

    if (!address) newErrors.address = 'Address is required';
    if (!name) newErrors.name = 'Name is required';
    if (!gender) newErrors.gender = 'Gender is required';

    if (!phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!isValidPhoneNumber(phone)) {
      newErrors.phone = 'Invalid phone number';
    }

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!birthday) newErrors.birthday = 'Birthday is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);


    try {
      const otherSocialString = socialHandles.length > 0
        ? socialHandles.map(s => `${s.platform}: ${s.handle}`).join(', ')
        : '';

      const dataToSubmit = {
        ...formData,
        other_social: otherSocialString || null,
        instagram: formData.instagram || null,
      };

      // 1️⃣ Check if email or phone already exists
      const checkRes = await axios.get(
        `https://tsoministries.com/wp-json/custom/v1/get-bdfs`
      );

      if (Array.isArray(checkRes.data)) {
        const duplicate = checkRes.data.find(
          (item: any) =>
            item.email?.toLowerCase() === dataToSubmit.email.toLowerCase() ||
            item.phone === dataToSubmit.phone
        );

        if (duplicate) {
          Toast.warning(
            "A record with this email or phone number already exists. You’ve already submitted your biodata."
          );
          setIsSubmitting(false);
          return;
        }
      }

      // 2️⃣ Submit to backend
      const res = await axios.post(
        `https://tsoministries.com/wp-json/custom/v1/submit-bdf/`,
        dataToSubmit,
        { headers: { "Content-Type": "application/json" } }
      );

      if (res.status === 200) {
        Toast.success("Biodata submitted successfully!");
        // optionally reset form
        setFormData({
          name: "",
          gender: "",
          phone: "",
          email: "",
          birthday: "",
          instagram: "",
          address: '',
          city: '',
          state: '',
          country: '',
        });
        setSocialHandles([]);
        setSubmitSuccess(true);
        setShowForm(false);
      } else {
        Toast.error("Something went wrong. Please try again.");
      }
    } catch (err: any) {
      console.error(err);
      Toast.error("An error occurred while submitting your data.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      name: '',
      gender: '',
      phone: '',
      email: '',
      birthday: '',
      instagram: '',
      other_social: '',
      address: '',
      city: '',
      state: '',
      country: '',
    });
    setSocialHandles([]);
    setSubmitSuccess(false);
    setShowForm(true);
  };

  return (
    <motion.div initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="relative overflow-hidden min-h-screen bg-gradient-to-b from-gray-900 to-black text-white py-8 px-4 sm:px-6 lg:px-8">
      <motion.img
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.4 }}
        transition={{ duration: 2 }}
        className='absolute top-0 left-0 bottom-0 h-full w-full'
        src={bgImage}
        style={{ objectFit: "cover", height: "170%" }}
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.4 }}
        transition={{ duration: 2 }}
        className='absolute top-0 left-0 bottom-0 h-full w-full'
        style={{ objectFit: "cover", height: "170%" }}
      />
      {/* Suplimentary information */}
      <motion.nav
        className="absolute top-28 right-1/4 pl-[10%] flex flex-wrap justify-center gap-6 md:gap-12 my-10 mb-2 text-sm tracking-widest"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 2.2 }}
      >
        <p className="text-md mt-2 text-gray-200">
          TSO Ministries (Titoluwa Olumideko Ministries) <br />
          <span style={{ fontSize: 11 }}>#Titoluwa Ministries, #TitoTheWriter, #TSO.</span>
        </p>
        <p className="text-md mt-2 text-gray-200 opacity-0">
          TSO Ministries (Titoluwa Olumideko Ministries) <br />
          Also known as Titoluwa Ministries, TitoTheWriter, and TSO.
          website link https://tsoministries.com/
        </p>
      </motion.nav>
      <div className="max-w-2xl mx-auto mt-20">
        <AnimatePresence mode='wait'>
          {showForm && !submitSuccess && (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -40, scale: 0.9 }}
              transition={{
                duration: 0.6,
                type: "spring",
                stiffness: 80,
                damping: 18,
              }}
              className="bg-white/5 rounded-3xl shadow-2xl overflow-hidden backdrop-blur-sm border border-amber-100/10"
            >
              <motion.div
                className="bg-gradient-to-r from-stone-600/5 to-cyan-500/35 px-8 py-10 text-white"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <User className="w-8 h-8" />
                  <h1 className="text-3xl font-bold">Bio Data Form</h1>
                </div>
                <p className="text-stone-100 text-sm">
                  Please fill in your information below
                </p>
              </motion.div>

              {/* Form Section */}
              <motion.form
                onSubmit={handleSubmit}
                className="p-8 space-y-6"
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: { opacity: 0 },
                  visible: {
                    opacity: 1,
                    transition: { staggerChildren: 0.05 },
                  },
                }}
              >
                {/* Each input block */}
                <motion.div
                  variants={{ hidden: { opacity: 0, y: 15 }, visible: { opacity: 1, y: 0 } }}
                  className="space-y-2"
                >
                  <label className="block text-sm font-semibold text-gray-200">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter your full name"
                    className={`bg-white/5 w-full px-4 py-3 border-2 ${errors.name ? 'border-red-300' : 'border-gray-200/10'} rounded-xl focus:border-stone-500/10 focus:ring-2 focus:ring-amber-200 outline-none transition-all`}
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </motion.div>

                <motion.div
                  variants={{ hidden: { opacity: 0, y: 15 }, visible: { opacity: 1, y: 0 } }}
                  className="space-y-2"
                >
                  <label className="block text-sm font-semibold text-gray-200">
                    Gender <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {['Male', 'Female'].map((gender) => (
                      <button
                        key={gender}
                        type="button"
                        onClick={() => handleInputChange('gender', gender)}
                        className={`py-3 rounded-xl font-medium transition-all border-2 ${formData.gender === gender
                          ? 'bg-stone-600 text-white border-orange-300 shadow-lg shadow-stone-500/30'
                          : 'bg-white/5 text-gray-200 border-gray-200/10 hover:border-amber-300/50'
                          }`}
                      >
                        {gender}
                      </button>
                    ))}
                  </div>
                  {errors.gender && <p className="text-red-500 text-xs mt-1">{errors.gender}</p>}
                </motion.div>

                <motion.div
                  variants={{ hidden: { opacity: 0, y: 15 }, visible: { opacity: 1, y: 0 } }}
                  className="grid sm:grid-cols-2 gap-6"
                >
                  <BDFPhoneInput
                    value={formData.phone}
                    onChange={(e: any) => handleInputChange('phone', e.target.value)}
                    errors={errors}
                  />

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-200">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="you@example.com"
                        className={`bg-white/5 w-full pl-11 pr-4 py-3 border-2 ${errors.email ? 'border-red-300' : 'border-gray-200/10'} rounded-xl focus:border-stone-500/10 focus:ring-2 focus:ring-amber-200 outline-none transition-all`}
                      />
                    </div>
                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>
                </motion.div>

                <motion.div
                  variants={{ hidden: { opacity: 0, y: 15 }, visible: { opacity: 1, y: 0 } }}
                  className="space-y-2"
                >
                  <label className="block text-sm font-semibold text-gray-200">
                    Birthday <span className="text-red-500">*</span>
                  </label>
                  <DatePicker
                    value={formData.birthday}
                    onChange={(value) => handleInputChange('birthday', value)}
                  />
                  {errors.birthday && <p className="text-red-500 text-xs mt-1">{errors.birthday}</p>}
                </motion.div>

                <div className='p-5 border border-gray-200/20 rounded-xl space-y-6'>
                  <motion.div
                    variants={{ hidden: { opacity: 0, y: 15 }, visible: { opacity: 1, y: 0 } }}
                    className="grid sm:grid-cols-3 gap-4"
                  >
                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-200">City</label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        placeholder="City"
                        className="bg-white/5 w-full px-4 py-3 border-2 border-gray-200/10 rounded-xl focus:border-stone-500/10 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-200">State</label>
                      <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                        placeholder="State"
                        className="bg-white/5 w-full px-4 py-3 border-2 border-gray-200/10 rounded-xl focus:border-stone-500/10 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-semibold text-gray-200">Country</label>
                      <input
                        type="text"
                        value={formData.country}
                        onChange={(e) => handleInputChange('country', e.target.value)}
                        placeholder="Country"
                        className="bg-white/5 w-full px-4 py-3 border-2 border-gray-200/10 rounded-xl focus:border-stone-500/10 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                      />
                    </div>
                  </motion.div>

                  <motion.div
                    variants={{ hidden: { opacity: 0, y: 15 }, visible: { opacity: 1, y: 0 } }}
                    className="space-y-2"
                  >
                    <label className="block text-sm font-semibold text-gray-200">
                      Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      placeholder="Enter your address"
                      className={`bg-white/5 w-full px-4 py-3 border-2 ${errors.address ? 'border-red-300' : 'border-gray-200/10'
                        } rounded-xl focus:border-stone-500/10 focus:ring-2 focus:ring-amber-200 outline-none transition-all`}
                    />
                    {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
                  </motion.div>
                </div>

                <motion.div
                  variants={{ hidden: { opacity: 0, y: 15 }, visible: { opacity: 1, y: 0 } }}
                  className="space-y-2"
                >
                  <label className="block text-sm font-semibold text-gray-200">
                    Instagram Handle
                  </label>
                  <div className="relative">
                    <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={formData.instagram}
                      onChange={(e) => handleInputChange('instagram', e.target.value)}
                      placeholder="@username"
                      className="bg-white/5 w-full pl-11 pr-4 py-3 border-2 border-gray-200/10 rounded-xl focus:border-stone-500/10 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                    />
                  </div>
                </motion.div>

                <motion.div
                  variants={{ hidden: { opacity: 0, y: 15 }, visible: { opacity: 1, y: 0 } }}
                  className="space-y-2"
                >
                  <label className="block text-sm font-semibold text-gray-200">
                    Other Social Media
                  </label>

                  {socialHandles.length > 0 && (
                    <div className="space-y-2">
                      {socialHandles.map((social, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between bg-gradient-to-r from-stone-50/30 to-cyan-50/30 p-3 rounded-xl border border-stone-100/10"
                        >
                          <span className="text-sm text-gray-200">
                            <span className="font-semibold">{social.platform}:</span> {social.handle}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveSocial(index)}
                            className="p-1 hover:bg-white/30 rounded-full transition-colors"
                          >
                            <X className="w-4 h-4 text-gray-50" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-200 hover:border-stone-400 hover:text-stone-600 hover:bg-stone-50 transition-all"
                  >
                    <Plus className="w-5 h-5" />
                    <span className="font-medium">Add Social Media Handle</span>
                  </button>
                </motion.div>


                <motion.button
                  type="submit"
                  disabled={isSubmitting || submitSuccess}
                  variants={{
                    hidden: { opacity: 0, y: 10 },
                    visible: { opacity: 1, y: 0 },
                  }}
                  transition={{ delay: 0.2 }}
                  className="w-full bg-gradient-to-r from-stone-600 to-cyan-500 text-white py-4 rounded-xl font-semibold text-lg hover:from-stone-700 hover:to-cyan-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-stone-500/30 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit Bio Data"
                  )}
                </motion.button>
              </motion.form>
            </motion.div>
          )}

          {/* ✅ Success Message */}
          {!showForm && submitSuccess && (
            <motion.div layout>
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.8, rotateX: 15, y: 40, height: 0 }}
                animate={{ opacity: 1, scale: 1, rotateX: 0, y: 0, height: 'auto' }}
                exit={{ opacity: 0, scale: 0.8, y: -40, height: 0 }}
                transition={{
                  duration: 0.7,
                  type: "spring",
                  stiffness: 90,
                  damping: 14,
                  delay: 2.5,
                }}
                className="bg-red-800 mb-2 rounded-3xl shadow-2xl overflow-hidden backdrop-blur-sm border border-amber-100/10">
                <p className='p-4'>
                  <span className='font-black'>Please Note</span>: Due to a system glitch, testimonies shared before Tuesday, October 7th were not received. We sincerely apologize and kindly ask that you resend them. We’ll greatly appreciate it!
                </p>
              </motion.div>

              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.8, rotateX: 15, y: 40, height: 0 }}
                animate={{ opacity: 1, scale: 1, rotateX: 0, y: 0, height: 'auto' }}
                exit={{ opacity: 0, scale: 0.8, y: -40, height: 0 }}
                transition={{
                  duration: 0.7,
                  type: "spring",
                  stiffness: 90,
                  damping: 14,
                  delay: 1.3,
                }}
                className="bg-white/75 mb-5 rounded-3xl shadow-2xl overflow-hidden backdrop-blur-sm border border-amber-100/10"
              >
                <motion.div
                  className="text-center mb-16 p-10 pb-0"
                >
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 3, repeat: Infinity }}
                    className="inline-block mb-6"
                  >
                    <div className="bg-gradient-to-r from-rose-500 to-pink-500 p-4 rounded-2xl shadow-lg">
                      <MessageCircle className="h-12 w-12 text-white" />
                    </div>
                  </motion.div>

                  <h2 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 bg-clip-text text-transparent mb-6">
                    Share your Testimonies
                  </h2>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                    Has your life been touched by this ministry? Share your testimony and let others be inspired by what God is doing through you!
                  </p>

                  {/* Stats */}
                  <motion.div
                    className="flex flex-wrap justify-center gap-8 mt-8"
                  >
                    <div className="flex items-center bg-white/80 backdrop-blur-sm rounded-full px-6 py-3 shadow-lg">
                      <Users className="h-5 w-5 text-rose-500 mr-2" />
                      <span className="font-semibold text-gray-700">{allUsers.length}+ Members</span>
                    </div>
                    <div className="flex items-center bg-white/80 backdrop-blur-sm rounded-full px-6 py-3 shadow-lg">
                      <TrendingUp className="h-5 w-5 text-pink-500 mr-2" />
                      <span className="font-semibold text-gray-700">100% Christo-centric</span>
                    </div>
                  </motion.div>
                </motion.div>

                {/* Call to Action */}
                <motion.div
                  className="text-center pb-10"
                >
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Link
                      to="/testimonials"
                      className="inline-flex items-center bg-gradient-to-r from-rose-500 via-pink-500 to-purple-500 text-white px-10 py-5 rounded-full font-bold text-lg shadow-2xl hover:shadow-rose-500/25 transition-all duration-300 group"
                    >
                      <span>Share Your Testimony</span>
                      <motion.div
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="ml-3"
                      >
                        <ArrowRight className="h-6 w-6 group-hover:translate-x-1 transition-transform" />
                      </motion.div>
                    </Link>
                  </motion.div>

                  <p className="text-gray-500 mt-4 text-sm">
                    Join thousands sharing their transformation stories
                  </p>
                </motion.div>
              </motion.div>

              <motion.div
                layout
                key="success"
                initial={{ opacity: 0, scale: 0.8, rotateX: 15 }}
                animate={{ opacity: 1, scale: 1, rotateX: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -40 }}
                transition={{
                  duration: 0.7,
                  type: "spring",
                  stiffness: 90,
                  damping: 14,
                }}
                className="bg-white/35 rounded-3xl shadow-2xl overflow-hidden backdrop-blur-sm border border-amber-100/10"
              >
                <motion.div
                  className="bg-gradient-to-r from-green-600/65 to-emerald-500/60 px-8 py-16 text-white text-center"
                  initial={{ opacity: 0, y: -30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.6 }}
                >
                  <div className="flex justify-center mb-6">
                    <motion.div
                      className="w-20 h-20 bg-white rounded-full flex items-center justify-center"
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{
                        repeat: Infinity,
                        duration: 2,
                        ease: easeInOut,
                      }}
                    >
                      <CheckCircle2 className="w-12 h-12 text-green-600" />
                    </motion.div>
                  </div>
                  <motion.h1
                    className="text-4xl font-bold mb-3"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    Success!
                  </motion.h1>
                  <motion.p
                    className="text-green-100 text-lg"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    Your bio data has been submitted successfully
                  </motion.p>
                </motion.div>

                <motion.div
                  className="p-8 text-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8 }}
                >
                  <p className="text-gray-200 mb-8">
                    Thank you for submitting your information. We have received your
                    bio data and will process it accordingly.
                  </p>
                  <motion.button
                    onClick={handleReset}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.97 }}
                    className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:from-blue-700 hover:to-cyan-600 transition-all shadow-lg shadow-blue-500/30"
                  >
                    <RefreshCcw className="w-5 h-5" />
                    Submit Another Form
                  </motion.button>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <SocialMediaModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={handleAddSocial}
      />
    </motion.div>
  );
}





// ---------------------------
// THPPhoneInput component
// ---------------------------
const BDFPhoneInput = ({ value, onChange, errors }: any) => {
  const [phone, setPhone] = useState(value || "");
  const [isValid, setIsValid] = useState(true);

  useEffect(() => {
    if (!phone) {
      setIsValid(true);
      // setForm((prev:any) => ({ ...prev, isPhoneValid: false }));
      return;
    }

    const valid = isValidPhoneNumber(phone);
    setIsValid(valid);

    // setForm((prev:any) => ({ ...prev, isPhoneValid: valid }));
  }, [phone]);

  const handleChange = (value: any) => {
    setPhone(value);
    onChange({ target: { name: "phone", value } });
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-gray-200">
        Phone Number <span className="text-red-500">*</span>
      </label>
      <div className="relative">
        <PhoneInput
          international
          defaultCountry="NG" // Nigeria by default
          value={phone}
          onChange={handleChange}
          // placeholder="+1 (555) 123-4567"
          className={`bg-white/5 w-full p-4 py-0 border-2 ${errors.phone ? 'border-red-300' : 'border-gray-200/10'} rounded-xl focus:border-stone-500/10 focus:ring-2 focus:ring-amber-200 outline-none transition-all`}
        />
        {phone && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {isValid ? (
              <CheckCircle2 className="text-green-500 w-5 h-5" />
            ) : (
              <XCircle className="text-red-500 w-5 h-5" />
            )}
          </div>
        )}
      </div>

      {!isValid && phone?.length > 0 && (
        <p className="text-red-500 text-sm mt-1">
          Invalid phone number, make sure your number is in the correct format +234 809 999 9999
        </p>
      )}
      {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
    </div>
  );
};